/**
 * 月次経理（FMT）のタスク一覧から、その月のタスクを起こす計算。
 *
 * シートの `予定営業日` は `04営業日` `毎月25日` `最終営業日` `役員会前` の 4 種類の書き方で
 * 入っている。これを実際の日付に直す。営業日は土日を除いた日で数える。
 *
 * 祝日はまだ考慮していない（休日カレンダーを持っていないため）。営業日の数え方を
 * 正確にするには、祝日の一覧をどこから取るかを決める必要がある。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 月次経理タスクの予定営業日を実際の日付に直し、その月のタスクを作る。
 * @graph-connects none
 */
import type { Task } from '@/tasks/task';
import type { MonthlyFmtTask } from '@cortex/ado-task-fmt';
import { MONTHLY_FMT, type FmtVariant } from './fmt-variant.js';

/** 月次経理で起こすタスクの種別 */
/** @graph-connects none */
export const MONTHLY_KIND = '月次経理';

/** @graph-connects none */
function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 何月分の月次かから、作業する月を返す。10 月分の月次は 11 月の営業日に締めるので、翌月にする
 * @graph-connects none
 */
export function workMonthOf(targetMonth: string): string {
  const [year, month] = targetMonth.split('-').map(Number);
  const next = new Date(Date.UTC(year ?? 1970, month ?? 1, 1));
  return `${String(next.getUTCFullYear())}-${String(next.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 指定した月の営業日（土日を除く日）を古い順に返す。
 * @graph-connects none
 */
export function businessDays(yearMonth: string): string[] {
  const [year, month] = yearMonth.split('-').map(Number);
  const safeYear = year ?? 1970;
  const safeMonth = month ?? 1;
  const lastDay = new Date(Date.UTC(safeYear, safeMonth, 0)).getUTCDate();
  const days: string[] = [];
  for (let day = 1; day <= lastDay; day += 1) {
    const weekday = new Date(Date.UTC(safeYear, safeMonth - 1, day)).getUTCDay();
    // 0 = 日曜、6 = 土曜
    if (weekday !== 0 && weekday !== 6) {
      days.push(`${yearMonth}-${pad(day)}`);
    }
  }
  return days;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 予定営業日の書き方を実際の日付に直す。その月に無い日は月末へ丸め、読み取れない書き方は最終営業日に寄せる。
 * @graph-connects businessDays [calls] その月の営業日を数える
 */
export function resolveDueDate(dueRule: string, yearMonth: string): string {
  const days = businessDays(yearMonth);
  const lastBusinessDay = days[days.length - 1] ?? `${yearMonth}-01`;

  const nth = /^(\d{1,2})営業日$/.exec(dueRule);
  if (nth !== null) {
    const index = Number(nth[1]) - 1;
    // その月の営業日数を超える指定は最終営業日に寄せる
    return days[index] ?? lastBusinessDay;
  }

  const fixed = /^毎月(\d{1,2})日$/.exec(dueRule);
  if (fixed !== null) {
    // その月に無い日（2 月の 31 日など）は月末へ丸める。存在しない日付を作ると、
    // 表示のときだけ翌月へ繰り上がって見え、並べ替えや期間の絞り込みとずれる
    const [year, month] = yearMonth.split('-').map(Number);
    const lastDay = new Date(Date.UTC(year ?? 1970, month ?? 1, 0)).getUTCDate();
    return `${yearMonth}-${pad(Math.min(Math.max(Number(fixed[1]), 1), lastDay))}`;
  }

  // 「最終営業日」「役員会前」など日付を決められない書き方は最終営業日に置く
  return lastBusinessDay;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 月次経理タスクの内容を「大分類_事業_中分類_内容」の形で作る。タスク側は分類の列を持たないため、ここで 1 つの文字列に畳み込む。空の区分は詰める
 * @graph-connects none
 */
export function buildTitle(task: MonthlyFmtTask): string {
  return [task.majorCategory, task.business, task.middleCategory, task.title]
    .filter((part) => part !== '')
    .join('_');
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 月次経理の一覧から、指定した月のタスクを作る。対応日は渡された対応表から取り、無いものだけ自動計算に落とす。種別は月次経理、状況は未着手
 * @graph-connects resolveDueDate [calls] 対応表に無い予定営業日を日付に直す
 * @graph-connects buildTitle [calls] 表示名を作る
 */
/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 一覧に出てくる予定営業日の種類を、営業日→固定日→その他の順で返す。日付を指定する画面の並びに使う
 * @graph-connects none
 */
export function listDueRules(fmtTasks: readonly MonthlyFmtTask[]): string[] {
  const rank = (rule: string): [number, number, string] => {
    const nth = /^(\d{1,2})営業日$/.exec(rule);
    if (nth !== null) {
      return [0, Number(nth[1]), rule];
    }
    const fixed = /^毎月(\d{1,2})日$/.exec(rule);
    if (fixed !== null) {
      return [1, Number(fixed[1]), rule];
    }
    return [2, 0, rule];
  };
  return [...new Set(fmtTasks.map((task) => task.dueRule))].sort((a, b) => {
    const [groupA, numberA, nameA] = rank(a);
    const [groupB, numberB, nameB] = rank(b);
    return groupA - groupB || numberA - numberB || nameA.localeCompare(nameB, 'ja');
  });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 予定営業日ごとの既定の日付を作る。何月分かの翌月で数える。土日だけを除いた営業日なので、祝日は人が直す前提の下書きとして使う
 * @graph-connects resolveDueDate [calls] 予定営業日を日付に直す
 * @graph-connects workMonthOf [calls] 作業する月を求める
 */
export function defaultDateByRule(
  targetMonth: string,
  rules: readonly string[],
): Record<string, string> {
  // 締めるのは翌月。10 月分の「04営業日」は 11 月の 4 営業日目になる
  const workMonth = workMonthOf(targetMonth);
  const dates: Record<string, string> = {};
  for (const rule of rules) {
    dates[rule] = resolveDueDate(rule, workMonth);
  }
  return dates;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business FMT の一覧から、何月分（決算なら決算月）を選んでタスクを作る。作業するのは翌月なので、対応日は翌月の営業日になる。種別は FMT の種類で決まり（月次経理／決算）、状況は未着手
 * @graph-connects resolveDueDate [calls] 対応表に無い予定営業日を日付に直す
 * @graph-connects buildTitle [calls] 内容を組み立てる
 * @graph-connects workMonthOf [calls] 作業する月を求める
 */
export function buildMonthlyTasks(
  fmtTasks: readonly MonthlyFmtTask[],
  yearMonth: string,
  dateByRule: Record<string, string> = {},
  // どの FMT から起こすか。決算タスクFMT なら種別は「決算」、ID の頭は closing になる
  variant: FmtVariant = MONTHLY_FMT,
): Task[] {
  // 締めるのは翌月。対応日は作業する月の営業日で決める
  const workMonth = workMonthOf(yearMonth);
  return fmtTasks.map((fmtTask) => ({
    id: `${variant.taskIdPrefix}-${yearMonth}-${fmtTask.id}`,
    team: 'sft' as const,
    kind: variant.kind,
    title: buildTitle(fmtTask),
    assignee: fmtTask.assignee,
    dueDate: dateByRule[fmtTask.dueRule] ?? resolveDueDate(fmtTask.dueRule, workMonth),
    status: '未着手' as const,
    // 何月分かは、作る時に選んだ対象月をそのまま持たせる
    targetMonth: yearMonth,
    // 分類は内容に畳み込んでいるので、タスク側が持つのは想定時間だけ
    estimatedHours: fmtTask.estimatedHours,
    // 手順書は型から写す。毎月同じ手順を、タスクを開いたその場で見られるようにする
    manual: fmtTask.manual,
    source: 'fmt' as const,
    sourceRef: fmtTask.id,
  }));
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business その月の FMT のタスク（月次経理／決算）を既に起こしているかを判定する。ID の頭に月が入っているので、絞り込みや対応日の指定を変えても同じ判定になる
 * @graph-connects none
 */
export function hasMonthlyTasks(
  existing: readonly Task[],
  yearMonth: string,
  variant: FmtVariant = MONTHLY_FMT,
): boolean {
  return existing.some((task) => task.id.startsWith(`${variant.taskIdPrefix}-${yearMonth}-`));
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 既にあるタスクと重ならない分だけを返す。同じ月に二重で起こさないため。
 * @graph-connects none
 */
export function withoutExisting(candidates: readonly Task[], existing: readonly Task[]): Task[] {
  const ids = new Set(existing.map((task) => task.id));
  return candidates.filter((task) => !ids.has(task.id));
}
